import { FormInstance } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
	Batch,
	FunctionaryInfo,
	Gender,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { SELECT_ROLE } from '../../../shared/Routes';
import { useBatchId } from '../../../hooks/useBatchId';
import { useCallApi } from '../../../hooks/useCallApi';
import { getBatch } from '../../../service/VolunteerApplicationService';
import { useTranslation } from 'react-i18next';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import { useConfirmPopup } from '../../../hooks/useConfirmPopup';
import { displayError, formatDate } from '../../../shared/Utils';
import {
	getZonesByRegion,
	getZonesBySrcmGroupId,
	registerFunctionary,
} from '../../../service/MySrcmService';
import {
	RegisterFunctionaryRequest,
	RoleAssignment,
	SaveFunctionaryRequest,
} from '../../../shared/MySrcm';
import {
	getLoggedInUserRoles,
	getRoles,
} from '../../../service/RoleBasedService';
import { Zone } from '../../../shared/ExamModuleTypes';
import { Region } from '../../../shared/PreWorkServiceTypes';
import { useAuth } from '../../../hooks/useAuth';
import useUserSession from '../../../hooks/useUserSession';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';

interface FormValues {
	firstName?: string;
	lastName?: string;
	email?: string;
	homeZone?: {
		regionId: number;
		regionName: string;
	};
	zoneHome?: string;
	mobile?: string;
	srcmId?: string;
	registerAs?: string;
	gender?: string;
	zone?: string[];
}

interface HomeZoneandRegion {
	homeZone?: string;
	homeRegion?: string;
	homeZoneId?: number;
	homeRegionId?: number;
}
export const useSelfRegistration = ({
	form,
}: {
	form?: FormInstance<FunctionaryInfo>;
} = {}) => {
	const [submitButtonDisabled, setSubmitButtonDisabled] =
		useState<boolean>(false);
	const { batchId, navigateToRoute } = useBatchId();
	const { mySrcmProfile, logout } = useAuth();
	const { callApi } = useCallApi();
	const { t } = useTranslation();
	const { confirmPopUpAction } = useConfirmPopup();
	const { setLoggedInUserRoles } = useUserSession();
	const { setLoading } = useLoadingIndicator();
	const { infoPopUpAction } = useInfoPopup();
	const [batchDetails, setBatchDetails] = useState<Batch | undefined>();
	const roleIdCacheRef = useRef<Map<string, number>>(new Map());

	const scrmGroupId = mySrcmProfile?.srcm_group?.id;

	const zoneAuthorizedFor = useCallback(
		async (authorizedRegionID: number) => {
			try {
				const response = await callApi(
					() => getZonesByRegion(authorizedRegionID),
					'errorOccurredWhileFetchingPreceptorDetails'
				);

				return response;
			} catch (error) {
				displayError(error);
			}
		},
		[callApi]
	);

	const getHomeZoneandRegion = useCallback(
		async (srcmGroupId: number): Promise<HomeZoneandRegion> => {
			const details: HomeZoneandRegion = {};

			const fetchRecursive = async (
				groupId: number
			): Promise<HomeZoneandRegion> => {
				const response = await callApi(
					() => getZonesBySrcmGroupId(groupId),
					'errorOccurredWhileFetchingPreceptorDetails'
				);

				if (!response) {
					throw new Error('Invalid API response');
				}

				const groupType = response?.group_type;

				if (groupType === 'zone' && !details.homeZone) {
					details.homeZone = response.name;
					details.homeZoneId = response.id;
				}

				if (groupType === 'region' && !details.homeRegion) {
					details.homeRegion = response.name;
					details.homeRegionId = response.id;
					return details;
				}

				if (response.parent) {
					return fetchRecursive(response.parent);
				} else {
					throw new Error('No parent found in group hierarchy');
				}
			};

			return fetchRecursive(srcmGroupId);
		},
		[callApi]
	);

	const fetchRoleIdByCode = async (
		srcmId: string,
		roleCode?: string
	): Promise<number | undefined> => {
		if (!roleCode || !srcmId) return;

		// 🔹 cache check
		const cached = roleIdCacheRef.current.get(roleCode);
		if (cached) return cached;

		const response = await getRoles({
			loggedInUserSrcmId: srcmId,
			selectedRoleCode: roleCode,
			filters: { roleCode },
			pageIndex: 0,
			pageSize: 1,
		});

		const roleId = response.roles?.[0]?.id;

		if (roleId) {
			roleIdCacheRef.current.set(roleCode, roleId);
		}

		return roleId;
	};

	const buildSaveFunctionaryRequest = useCallback(
		async (
			functionaryInfo: FunctionaryInfo
		): Promise<SaveFunctionaryRequest | undefined> => {
			const selectedRegisterRole = localStorage.getItem('registerRole');
			const formData = form?.getFieldsValue() as FormValues;

			const homeDetails = await getHomeZoneandRegion(Number(scrmGroupId));

			if (!selectedRegisterRole) return;

			const roleCodeMap: Record<string, string> = {
				proposer: UserRole.PROPOSER,
				preworkMentor: UserRole.PREWORK_MENTOR,
			};

			const roleCode = roleCodeMap[selectedRegisterRole];

			const roleId = await fetchRoleIdByCode(functionaryInfo.srcmId, roleCode);

			if (!roleId) {
				throw new Error('RoleId not found');
			}

			const regionId = homeDetails.homeRegionId;

			if (!regionId) {
				throw new Error('Missing regionId');
			}

			const response = await zoneAuthorizedFor(regionId);

			const zoneList = response?.results ?? [];

			const zoneMap = new Map(zoneList.map((z) => [z.name, z.id]));

			const authorizedZones: Zone[] = (formData.zone || []).map((zoneName) => ({
				zoneId: zoneMap.get(zoneName) ?? 0,
				zoneName,
			}));

			const authorizedRegions: Region[] = formData.homeZone
				? [
						{
							regionId: homeDetails.homeRegionId ?? 0,
							regionName: homeDetails.homeRegion ?? '',
						},
				  ]
				: [];
			const roleAssignment: RoleAssignment = {
				roleId,
				roleCode,
				authorizedRegions,
				authorizedZones,
				startDate: formatDate(new Date()),
				endDate: formatDate(new Date('9999-12-12')),
			};

			return {
				name: functionaryInfo.printName ?? '',
				email: functionaryInfo.email ?? '',
				mobile: functionaryInfo.mobile ?? '',
				homeRegion: functionaryInfo.homeRegion ?? '',
				homeZone: functionaryInfo.homeZone ?? '',
				partnerId: functionaryInfo.partnerId ?? 0,
				srcmId: functionaryInfo.srcmId,
				gender: functionaryInfo.gender as Gender,
				roleAssignments: [roleAssignment],
				isActive: true,
			};
		},
		[form, getHomeZoneandRegion, zoneAuthorizedFor, scrmGroupId]
	);

	const onConfirmSubmit = useCallback(async () => {
		const formValues = form?.getFieldsValue() as FormValues;
		const firstName = formValues.firstName?.trim() || '';
		const lastName = formValues.lastName?.trim() || '';
		const name = `${firstName} ${lastName}`.trim();

		const updatedFunctionary = {
			printName: name,
			homeZone: formValues?.homeZone?.regionName,
			email: formValues?.email,
			srcmId: formValues?.srcmId as string,
			mobile: formValues?.mobile,
			homeRegion: formValues?.zoneHome,
			gender: formValues?.gender,
			zone: formValues?.zone || [],
			partnerId: 0,
		};

		if (updatedFunctionary) {
			const saveFunctionaryRequest = await buildSaveFunctionaryRequest(
				updatedFunctionary
			);

			if (!saveFunctionaryRequest) {
				setLoading(false);
				return;
			}

			const bulkRequest: RegisterFunctionaryRequest = {
				saveFunctionaryRequest,
			};

			try {
				await callApi(
					() => registerFunctionary(bulkRequest),
					'errorOccurredWhileSubmittingApplication'
				);
				const successContent = t('successfullyRegistered');
				const srcmId = updatedFunctionary.srcmId;
				const handleSuccess = async () => {
					const userRoles = await getLoggedInUserRoles({
						loggedInUserSrcmId: srcmId,
					});
					setLoggedInUserRoles(userRoles.rolesDetails);
					await logout?.();
				};
				infoPopUpAction(
					successContent,
					'automaticallyLoggedOut',
					() => {
						void handleSuccess();
					},
					ConfirmTick
				);
			} catch (error) {
				displayError(error);
			} finally {
				setLoading(false);
			}
		}
	}, [
		buildSaveFunctionaryRequest,
		callApi,
		infoPopUpAction,
		t,
		form,
		setLoggedInUserRoles,
		logout,
		setLoading,
	]);

	const onSubmitClick = useCallback(() => {
		confirmPopUpAction(
			'confirmSubmit',
			'areYouSureYouWantToSubmit',
			onConfirmSubmit
		);
	}, [onConfirmSubmit, confirmPopUpAction]);

	const onCancelClick = useCallback(() => {
		navigateToRoute(SELECT_ROLE);
	}, [navigateToRoute]);

	const handleFormValuesChange = useCallback(async () => {
		try {
			await form?.validateFields({ validateOnly: true });
			setSubmitButtonDisabled(false);
		} catch (e) {
			const fields = e as { values: FunctionaryInfo; outOfDate: boolean };
			if (!fields.outOfDate) {
				setSubmitButtonDisabled(true);
			}
		}
	}, [form]);

	const getBatchDetails = useCallback(async () => {
		const batch = await getBatch({ value: batchId });
		setBatchDetails(batch);
	}, [batchId]);

	useEffect(() => {
		void getBatchDetails();
	}, [getBatchDetails]);

	return {
		form,
		submitButtonDisabled,
		onSubmitClick,
		onCancelClick,
		handleFormValuesChange,
		zoneAuthorizedFor,
		getHomeZoneandRegion,
		batchDetails,
	};
};
